import { Logger } from "./logger";
import {
  EVENT_VALID_TYPES,
  EVENT_MAX_QUEUE_SIZE,
  EVENT_FLUSH_INTERVAL_MS,
  EVENT_BEACON_MAX_BYTES,
} from "./config";
import type { BatchEvent } from "../types";

let queue: BatchEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let isFlushing = false;
let isDestroyed = false;
let flushHandler: ((events: BatchEvent[]) => Promise<boolean>) | null = null;

let beforeUnloadListener: (() => void) | null = null;
let visibilityChangeListener: (() => void) | null = null;

function removeUnloadListeners(): void {
  if (typeof window === "undefined") return;
  if (beforeUnloadListener) {
    window.removeEventListener("beforeunload", beforeUnloadListener);
    beforeUnloadListener = null;
  }
  if (visibilityChangeListener) {
    document.removeEventListener("visibilitychange", visibilityChangeListener);
    visibilityChangeListener = null;
  }
}

function beaconFlush(url: string): void {
  if (queue.length === 0) return;

  const initialSize = JSON.stringify({ events: queue }).length;
  if (initialSize > EVENT_BEACON_MAX_BYTES) {
    Logger.warn(`Beacon payload too large (${initialSize} bytes), trimming`);
    while (queue.length > 0) {
      if (JSON.stringify({ events: queue }).length <= EVENT_BEACON_MAX_BYTES) break;
      queue.shift();
    }
  }

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      const blob = new Blob([JSON.stringify({ events: queue })], {
        type: "application/json",
      });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) {
        Logger.log(`Beacon flushed ${queue.length} events`);
        queue = [];
      } else {
        Logger.warn("sendBeacon returned false — events retained");
      }
    } catch {
      Logger.warn("sendBeacon failed — events retained");
    }
  } else {
    Logger.warn("sendBeacon not available — events retained for next flush");
  }
}

export const EventQueue = {
  init(onFlush: (events: BatchEvent[]) => Promise<boolean>, beaconUrl?: string): void {
    if (isDestroyed) {
      Logger.warn("EventQueue already destroyed — cannot reinitialize");
      return;
    }

    flushHandler = onFlush;

    if (typeof window !== "undefined" && flushTimer === null) {
      flushTimer = setInterval(() => {
        this.flush();
      }, EVENT_FLUSH_INTERVAL_MS);

      if (beaconUrl) {
        removeUnloadListeners();
        const url = beaconUrl;
        beforeUnloadListener = () => beaconFlush(url);
        visibilityChangeListener = () => {
          if (document.visibilityState === "hidden") {
            beaconFlush(url);
          }
        };
        window.addEventListener("beforeunload", beforeUnloadListener);
        document.addEventListener("visibilitychange", visibilityChangeListener);
      }

      Logger.log(`EventQueue initialized — auto-flush every ${EVENT_FLUSH_INTERVAL_MS / 1000}s`);
    }
  },

  push(type: string, data: Record<string, unknown>): void {
    if (isDestroyed) {
      Logger.warn("EventQueue destroyed — event rejected");
      return;
    }

    if (!EVENT_VALID_TYPES.has(type)) {
      Logger.warn(`Invalid event type rejected: ${type}`);
      return;
    }

    if (queue.length >= EVENT_MAX_QUEUE_SIZE) {
      const dropped = queue.shift();
      Logger.warn(`Queue full (${EVENT_MAX_QUEUE_SIZE}) — dropped oldest event: ${dropped?.type}`);
    }

    const event: BatchEvent = {
      type,
      timestamp: Date.now(),
      data,
    };

    queue.push(event);
    Logger.log(`Event queued: ${type} (${queue.length} in queue)`);
  },

  async flush(): Promise<void> {
    if (isDestroyed || isFlushing || queue.length === 0 || !flushHandler) return;

    isFlushing = true;
    const batch = [...queue];

    try {
      const accepted = await flushHandler(batch);
      if (accepted) {
        queue.splice(0, batch.length);
        Logger.log(`Flushed ${batch.length} events successfully`);
      } else {
        Logger.warn(`Flush rejected by server — ${batch.length} events retained`);
      }
    } catch (err) {
      Logger.error("Flush failed — events retained for retry", err);
    } finally {
      isFlushing = false;
    }
  },

  size(): number {
    return queue.length;
  },

  async destroy(): Promise<void> {
    if (isDestroyed) return;

    if (queue.length > 0 && flushHandler) {
      await this.flush();
    }

    if (flushTimer !== null) {
      clearInterval(flushTimer);
      flushTimer = null;
    }

    removeUnloadListeners();

    isDestroyed = true;
    flushHandler = null;
    queue = [];
    Logger.log("EventQueue destroyed");
  },

  reset(): void {
    if (flushTimer !== null) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
    removeUnloadListeners();
    queue = [];
    isFlushing = false;
    isDestroyed = false;
    flushHandler = null;
  },
};
